import React, { useCallback, useRef, useState } from 'react';

import { Modal, ModalBody, ModalHeader } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { ShareAsset, ShareAssetSelection } from '../../../../../shared/types';
import { useShareEducationModalContext } from '../../shareEducationModal';
import type { OpenModalResult } from '../../shareEducationModal';

import { EducationModalBody } from './components/EducationModalBody';

export type UseStoriesEducationModalProps = {
	onContinueAction: (selectedIndex: number, hasInteracted: boolean) => Promise<void>;
	shareVisualPreview: File | string | null;
	/** Present only in the asset-picker arms (CHANGE-95456); absent renders the single-asset modal. */
	shareVisualPreviews?: readonly ShareAsset[];
	onAssetSelected?: (selection: ShareAssetSelection) => void;
	/** Undebounced position updates, so a close cannot beat the selection debounce. */
	onActiveAssetChange?: (selectedIndex: number) => void;
	/**
	 * Which picker to render once there is more than one preview. Absent falls back to the carousel, so
	 * variant 1 — which shipped before this prop existed — keeps its layout without passing anything.
	 * Whether a picker renders at all is decided by the preview count, not by this.
	 */
	pickerLayout?: 'carousel' | 'swatches';
};

export type OpenStoriesEducationModalFn = (options: UseStoriesEducationModalProps) => OpenModalResult;

/**
 * Returns [openModal, modal | null].
 *
 * When `ShareEducationModalProvider` is present in the tree the hook delegates
 * to the shared modal (modal === null, rendering is handled by ShareSlideUp).
 * Otherwise it falls back to a locally-managed modal instance.
 */
export function useEducationModal(): [openModal: OpenStoriesEducationModalFn, modal: React.ReactElement | null] {
	const openSharedModal = useShareEducationModalContext();
	const { translate } = useI18n();

	const heading = translate('corgi.psf.share.modals.heading.title', { shareChannel: 'Instagram' });

	// Local fallback
	const [localIsOpen, setLocalIsOpen] = useState(false);
	const [localOptions, setLocalOptions] = useState<UseStoriesEducationModalProps | null>(null);
	const localResolverRef = useRef<((result: { successfulShare: boolean } | undefined) => void) | null>(null);

	const localHandleClose = useCallback((result?: { successfulShare: boolean }) => {
		setLocalIsOpen(false);

		localResolverRef.current?.(result);
		localResolverRef.current = null;
	}, []);

	const localOpenModal = useCallback((options: UseStoriesEducationModalProps): OpenModalResult => {
		setLocalOptions(options);
		setLocalIsOpen(true);

		/**
		 * Need to resolve any prior pending promise so it doesn't hang
		 * in case users open the modal multiple times.
		 */
		localResolverRef.current?.(undefined);

		// oxlint-disable-next-line promise/avoid-new
		const promise = new Promise<{ successfulShare: boolean } | undefined>((resolve) => {
			localResolverRef.current = resolve;
		});

		return { waitForResult: async () => promise };
	}, []);

	// Shared-context path — builds Instagram Stories content and delegates to shared modal.
	const sharedOpenModal = useCallback(
		(options: UseStoriesEducationModalProps): OpenModalResult => {
			return openSharedModal
				? openSharedModal((close) => ({
						heading,
						body: (
							<EducationModalBody
								onContinue={async (selectedIndex, hasInteracted) => {
									await options.onContinueAction(selectedIndex, hasInteracted);
									close({ successfulShare: true });
								}}
								shareVisualPreview={options.shareVisualPreview}
								shareVisualPreviews={options.shareVisualPreviews}
								onAssetSelected={options.onAssetSelected}
								onActiveAssetChange={options.onActiveAssetChange}
								pickerLayout={options.pickerLayout}
							/>
						),
					}))
				: { waitForResult: async () => undefined };
		},
		[openSharedModal, heading],
	);

	if (openSharedModal) {
		return [sharedOpenModal, null];
	}

	const localModal = (
		<Modal
			isOpen={localIsOpen}
			onClose={() => localHandleClose(undefined)}
			onOpenChange={(open) => {
				if (!open) localHandleClose(undefined);
			}}
			transformToSheet
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
		>
			<ModalHeader title={heading} />
			<ModalBody>
				{localOptions && (
					<EducationModalBody
						onContinue={async (selectedIndex, hasInteracted) => {
							await localOptions.onContinueAction(selectedIndex, hasInteracted);
							localHandleClose({ successfulShare: true });
						}}
						shareVisualPreview={localOptions.shareVisualPreview}
						shareVisualPreviews={localOptions.shareVisualPreviews}
						onAssetSelected={localOptions.onAssetSelected}
						onActiveAssetChange={localOptions.onActiveAssetChange}
						pickerLayout={localOptions.pickerLayout}
					/>
				)}
			</ModalBody>
		</Modal>
	);

	return [localOpenModal, localModal];
}
